import { client } from '/api/client.gen';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'axios';

import { authCheckerTimeout } from '/shared/util/Statics';

import hasSessionExpired from '/shared/functions/hasSessionExpired';

import { getSession, fetchAuth } from '/business/shared/state/actions/auth';
import getSessionTokenSelector from '/shared/state/selectors/getSessionTokenSelector';
import getSessionExpiresSelector from '/shared/state/selectors/getSessionExpiresSelector';
import type { LegacyDispatch } from '/business/shared/state/actions/types';

/**
 * Start an interval to check the current session and authentication.
 *
 * @param {function} dispatch
 *   Redux dispatch.
 * @param {string} token
 *   The session token.
 * @param {string} expires
 *   A date for session expiry.
 * @return {null}
 */
export function AuthChecker() {
  const dispatch = useDispatch<LegacyDispatch>();
  const token = useSelector(getSessionTokenSelector);
  const expires = useSelector(getSessionExpiresSelector);

  const expiresRef = useRef(expires);
  expiresRef.current = expires;

  useEffect(() => {
    if (token) {
      const authHeader = `Bearer ${token}`;
      axios.defaults.headers.Authorization = authHeader;
      client.setConfig({
        headers: {
          Authorization: authHeader,
        },
      });
    } else {
      delete axios.defaults.headers.Authorization;
      client.setConfig({
        headers: {
          Authorization: null,
        },
      });
    }
  }, [token]);

  const checkSession = useCallback(async () => {
    const hasExpired = hasSessionExpired(expiresRef.current);
    try {
      if (hasExpired) {
        await dispatch(getSession());
      }
    } catch (error) {
      // Failed to get session.
    }
  }, [dispatch]);

  const checkAuth = useCallback(
    async (first = false) => {
      try {
        await dispatch(fetchAuth());
      } catch (error) {
        // Failed to get auth.
      }
    },
    [dispatch],
  );

  useEffect(() => {
    checkSession();
    checkAuth(true);
    const timer = setInterval(() => {
      checkSession();
      checkAuth();
    }, authCheckerTimeout);
    return () => clearInterval(timer);
  }, [checkSession, checkAuth]);

  return null;
}

export default AuthChecker;
